import { Operator } from "@/types/insights/operators"
import { TenantFeatures } from "./tenantFeatures"
import { CaseColumn } from "./caseColumns"

export interface Rule {
	id?: string;
	title: string;
	triggers: Trigger[];
	actions: RuleAction[];
	tenantId: string;
	runOnce: boolean;
	applyToTemplates: RuleTemplate [];
}

export interface Trigger {
	id?: string;
	label: string;
	field: string;
	subField: string;
	operator: Operator;
	fieldDataType: number;
	fieldTypeId: string;
	value: string;
}

export interface RuleAction {
	id: string | null;
	key?: string;

	label: string;
	actionType: ActionType;
	settings: { [key: string]: string | {} };
}

export interface RuleActionDto extends RuleAction {
	fieldData: CaseColumn | null
	dynamic: boolean
}

export enum ActionType {
	SetValue,
	AddTeam,
	SetFieldRequired,
	SendHttp,
	SendEmail,
	AddField,
	SentToIntegration,
	SendNotification,
	Close
}

export interface ActionTypeLabel {
	label: string;
	value: number;
	requireFeature?: TenantFeatures
}

const actionTypeLabels: ActionTypeLabel[] = [
	{
		label: "Set Value",
		value: ActionType.SetValue
	},
	{
		label: "Add Role",
		value: ActionType.AddTeam
	},
	{
		label: "Add Field",
		value: ActionType.AddField
	},
	{
		label: "Set Field Required",
		value: ActionType.SetFieldRequired
	},
	{
		label: "Send HTTPS",
		value: ActionType.SendHttp
	},
	{
		label: "Send Email",
		value: ActionType.SendEmail
	},
	{
		label: "Send to Integration",
		value: ActionType.SentToIntegration
	},
	{
		label: "Send Notification",
		value: ActionType.SendNotification,
		requireFeature: TenantFeatures.NotificationActions
	},
	{
		label: "Close Service",
		value: ActionType.Close,
		requireFeature: TenantFeatures.CloseRecordRuleAction
	}
]

// Defines all the different action setting field
export enum ActionSettingField {
	Field,
	FieldDataType,
	NewValue,
	NewValueLabel,
	FieldId,
	Required,
	TeamId,
	TeamTitle,
	Recipient,
	Subject,
	Section,
	DynamicValue,
	ValueFormat,
	IncludeAttachments,
	URL,
	HttpHeaders,
	Integration,
	RecipientList,
	Template,
	TemplateId,
	Urgency,
	TimeZone,
	CCList,
	ExcludeRecipientList,
	CloseOption,
	NewValueSetting
}

export enum NewValueSetting {
	Manual,
	Field,
	Expression
}

interface ActionSetting {
	id: string;
	type: ActionSettingField;
	label: string;
	visible: boolean;
	validation: any;
	show?: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => boolean
}

// Definitions for action settings

// Constants for action setting IDs to avoid magic strings
// to match with the RuleActionSettingsKeys in Data
export const ActionSettingIds = {
	CCList: "ccList",
	CloseOption: "closeOption",
	DynamicValue: "dynamicValue",
	ExcludeRecipientList: "excludeRecipientList",
	Field: "field",
	FieldDataType: "fieldDataType",
	FieldId: "fieldId",
	HttpHeaders: "httpHeaders",
	IncludeAttachments: "includeAttachments",
	Integration: "integration",
	NewValue: "newValue",
	NewValueLabel: "newValueLabel",
	NewValueSetting: "newValueSetting",
	Recipient: "recipient",
	RecipientList: "recipientList",
	Required: "required",
	Subject: "subject",
	Section: "section",
	TeamId: "teamId",
	TeamTitle: "teamTitle",
	Template: "template",
	TemplateId: "templateId",
	TimeZone: "timeZone",
	Urgency: "urgency",
	Url: "url",
	ValueFormat: "valueFormat"
} as const

const actionSetting: ActionSetting[] = [
	{ id: ActionSettingIds.Field, type: ActionSettingField.Field, label: "Field", visible: true, validation: null },
	{ id: ActionSettingIds.FieldDataType, type: ActionSettingField.FieldDataType, label: "Data Type", visible: false, validation: null },
	{ id: ActionSettingIds.NewValue, type: ActionSettingField.NewValue, label: "New Value", visible: true, validation: null },
	{ id: ActionSettingIds.NewValueLabel, type: ActionSettingField.NewValueLabel, label: "New value label", visible: false, validation: null },
	{ id: ActionSettingIds.DynamicValue, type: ActionSettingField.DynamicValue, label: "Dynamic Value", visible: false, validation: null },
	{ id: ActionSettingIds.ValueFormat, type: ActionSettingField.ValueFormat, label: "Value Format", visible: false, validation: null },
	{ id: ActionSettingIds.FieldId, type: ActionSettingField.FieldId, label: "Field", visible: true, validation: null },
	{ id: ActionSettingIds.Required, type: ActionSettingField.Required, label: "Required", visible: true, validation: null },
	{ id: ActionSettingIds.IncludeAttachments, type: ActionSettingField.IncludeAttachments, label: "Include Attachments", visible: true, validation: null },
	{ id: ActionSettingIds.TeamId, type: ActionSettingField.TeamId, label: "Team", visible: true, validation: null },
	{ id: ActionSettingIds.TeamTitle, type: ActionSettingField.TeamTitle, label: "Team Title", visible: false, validation: null },
	{ id: ActionSettingIds.Recipient,
		type: ActionSettingField.Recipient,
		label: "Recipient",
		visible: true,
		validation: null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return actionType === ActionType.SendEmail ? !featureEnabled(TenantFeatures.AdvancedEmailRule) : true // Hide for email rules if advanced feature is on
		} },
	{ id: ActionSettingIds.Subject, type: ActionSettingField.Subject, label: "Subject", visible: true, validation: null },
	{ id: ActionSettingIds.Section, type: ActionSettingField.Section, label: "Section", visible: true, validation: null },
	{ id: ActionSettingIds.Url, type: ActionSettingField.URL, label: "URL", visible: true, validation: (item: any) => item.toLowerCase().startsWith("https:") ? null : "The supplied URL must be valid 'https:' URL" },
	{ id: ActionSettingIds.HttpHeaders, type: ActionSettingField.HttpHeaders, label: "Headers", visible: true, validation: null },
	{ id: ActionSettingIds.Integration, type: ActionSettingField.Integration, label: "Send to", visible: true, validation: null },
	{ id: ActionSettingIds.RecipientList,
		type: ActionSettingField.RecipientList,
		label: "Recipients",
		visible: true,
		validation: null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return actionType === ActionType.SendEmail ? featureEnabled(TenantFeatures.AdvancedEmailRule) : true // Hide for email rules if advanced feature is off
		}
	},
	{ id: ActionSettingIds.ExcludeRecipientList,
		type: ActionSettingField.ExcludeRecipientList,
		label: "Exclude Recipients",
		visible: true,
		validation: null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return actionType === ActionType.SendEmail ? featureEnabled(TenantFeatures.EmailExcludeRecipients) : true // Hide for email rules if advanced feature is off
		}
	},
	{ id: ActionSettingIds.CCList,
		type: ActionSettingField.CCList,
		label: "CC",
		visible: true,
		validation: null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return actionType === ActionType.SendEmail ? (featureEnabled(TenantFeatures.AdvancedEmailRule) && featureEnabled(TenantFeatures.AdvancedEmailRuleCC)) : true // Hide for email rules if advanced feature is off or if the EmailCC feature is disabled
		}
	},
	{ id: ActionSettingIds.TemplateId,
		type: ActionSettingField.TemplateId,
		label: "Format",
		visible: true,
		validation: null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return true || featureEnabled(TenantFeatures.EmailFormats) // Hide for email rules if feature is off
		} },
	{ id: ActionSettingIds.Template,
		type: ActionSettingField.Template,
		label: "Template",
		visible: true,
		validation: (item: any, settings: any) => (item == null || item === "") && !settings.templateId ? "Please enter a template" : null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return actionType === ActionType.SendEmail ? featureEnabled(TenantFeatures.AdvancedEmailRule) : true // Hide for email rules if advanced feature is off
		} },
	{ id: ActionSettingIds.Urgency, type: ActionSettingField.Urgency, label: "Urgency", visible: true, validation: null },
	{ id: ActionSettingIds.TimeZone,
		type: ActionSettingField.TimeZone,
		label: "TimeZone",
		visible: true,
		validation: null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return actionType === ActionType.SendEmail ? featureEnabled(TenantFeatures.AdvancedEmailRule) : true // Hide for email rules if advanced feature is off
		}
	},
	{ id: ActionSettingIds.CloseOption,
		type: ActionSettingField.CloseOption,
		label: "Close Service Options",
		visible: true,
		validation: null,
		show: (actionType: ActionType, featureEnabled: (feature: TenantFeatures) => boolean) => {
			return actionType === ActionType.Close ? featureEnabled(TenantFeatures.CloseRecordRuleAction) : true // Allow closing record without filling out requried fields
		}
	},
	{ id: ActionSettingIds.NewValueSetting,
		type: ActionSettingField.NewValueSetting,
		label: "New Value Setting",
		visible: true,
		validation: null
	}
]

// Definition of the fields that should be filled in when an Action Setting Type is used
const actionSettingsForActionType: { [index: number]: ActionSettingField[] } = {}

actionSettingsForActionType[ActionType.SetValue] =
[
	ActionSettingField.Field,
	ActionSettingField.FieldDataType,
	ActionSettingField.NewValueSetting,
	ActionSettingField.NewValue,
	ActionSettingField.NewValueLabel,
	ActionSettingField.DynamicValue,
	ActionSettingField.ValueFormat
]
actionSettingsForActionType[ActionType.SetFieldRequired] = [ActionSettingField.FieldId, ActionSettingField.Required]
actionSettingsForActionType[ActionType.AddTeam] = [ActionSettingField.TeamId, ActionSettingField.TeamTitle]
actionSettingsForActionType[ActionType.SendHttp] = [ActionSettingField.URL, ActionSettingField.HttpHeaders]
actionSettingsForActionType[ActionType.SendEmail] =
[
	ActionSettingField.Subject,
	ActionSettingField.Recipient,
	ActionSettingField.RecipientList,
	ActionSettingField.ExcludeRecipientList,
	ActionSettingField.CCList,
	ActionSettingField.TemplateId,
	ActionSettingField.Template,
	ActionSettingField.IncludeAttachments
]
actionSettingsForActionType[ActionType.AddField] = [ActionSettingField.FieldId, ActionSettingField.Required, ActionSettingField.Section]
actionSettingsForActionType[ActionType.SentToIntegration] = [ActionSettingField.Integration]
actionSettingsForActionType[ActionType.SendNotification] =
[
	ActionSettingField.Subject,
	ActionSettingField.Urgency,
	ActionSettingField.RecipientList,
	ActionSettingField.Template,
	ActionSettingField.TimeZone
]
actionSettingsForActionType[ActionType.Close] = [ActionSettingField.CloseOption]

export enum RecipientType {
	User,
	Team,
	UserRole,
	EmailAddress
}

export const RecipientTypeLabels = [
	{
		key: RecipientType.User,
		label: "User"
	},
	{
		key: RecipientType.Team,
		label: "Role"
	},
	{
		key: RecipientType.UserRole,
		label: "User Field"
	},
	{
		key: RecipientType.EmailAddress,
		label: "Email Address"
	}
]

export interface Recipient {
	key: string
	type: RecipientType
	id?: string
	name?: string
	emailAddress?: string
}

export interface RuleTemplate {
	id: string | null,
	title: string,
	enabledOnRecord: boolean|null,
	enabledOnCase: boolean|null
}

export enum CloseOptions {
	Immediate,
	PromptUser
}

export const CloseOptionsLabels = [
	{
		key: CloseOptions.Immediate,
		label: "Immediate"
	},
	{
		key: CloseOptions.PromptUser,
		label: "Prompt user"
	}
]
export { actionTypeLabels, actionSettingsForActionType, actionSetting, ActionSetting }
